<template>
  <div v-if="visible" class="disk-group-nav">
    <!-- 收起栏 -->
    <div v-if="!expanded" class="disk-group-nav-bar">
      <div class="disk-group-nav-bar-left">
        <Icon type="ios-compass"></Icon>
        <span>资料导航</span>
      </div>
      <a class="disk-group-nav-link" @click.prevent="expand">展开</a>
    </div>

    <!-- 展开面板 -->
    <div v-else class="disk-group-nav-panel">
      <div class="disk-group-nav-title">
        <strong>教研群资料导航</strong>
        <a class="disk-group-nav-link" @click.prevent="collapse">收起</a>
      </div>

      <div class="disk-group-nav-cats">
        <span
          v-for="cat in categories"
          :key="cat.key"
          :class="['disk-group-nav-chip', { on: categoryKey === cat.key }]"
          @click="toggleCategory(cat.key)">
          {{ cat.title }}
          <a class="disk-group-nav-chip-open" @click.stop.prevent="openNode(cat.targetNodeId)" title="打开此分类">打开</a>
        </span>
      </div>

      <div class="disk-group-nav-search">
        <Input
          v-model="keyword"
          size="small"
          clearable
          placeholder="搜索导航入口"
          style="width: 260px"
          @on-change="onKeywordChange">
        </Input>
        <a v-if="hasFilter" class="disk-group-nav-link" style="margin-left: 12px" @click.prevent="clearFilter">查看全部</a>
      </div>

      <div v-if="loading" class="disk-group-nav-empty">加载导航…</div>
      <div v-else-if="loadError" class="disk-group-nav-empty">
        导航加载失败，请稍后重试
        <a class="disk-group-nav-link" style="margin-left: 8px" @click.prevent="reload">重试</a>
      </div>
      <div v-else-if="!entries.length" class="disk-group-nav-empty">暂无你有权访问的导航入口</div>
      <div v-else-if="!filteredEntries.length" class="disk-group-nav-empty">没有匹配的入口，请更换关键词或查看全部</div>
      <div v-else class="disk-group-nav-cards">
        <div
          v-for="item in filteredEntries"
          :key="item.key"
          class="disk-group-nav-card"
          @click="openNode(item.targetNodeId)">
          <div class="disk-group-nav-card-title">{{ item.title }}</div>
          <div class="disk-group-nav-card-summary">{{ item.summary }}</div>
          <div class="disk-group-nav-card-path">{{ item.pathLabel || './' }}</div>
          <div class="disk-group-nav-card-action">
            <Button size="small" type="primary" :loading="openingId === item.targetNodeId" @click.stop="openNode(item.targetNodeId)">打开文件夹</Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'DiskGroupNav',
    props: {
      groupId: {
        type: [Number, String],
        default: null
      },
      rootNodeId: {
        type: [Number, String],
        default: null
      },
      currentFolderId: {
        type: [Number, String],
        default: null
      }
    },
    data () {
      return {
        loading: false,
        loadError: false,
        categories: [],
        entries: [],
        categoryKey: '',
        keyword: '',
        expanded: true,
        userCollapsed: false,
        openingId: null,
        openSeq: 0,
        loadedGroupId: null
      }
    },
    computed: {
      visible () {
        return Number(this.groupId) === 1
      },
      atRoot () {
        if (this.rootNodeId == null || this.currentFolderId == null) {
          return false
        }
        return Number(this.rootNodeId) === Number(this.currentFolderId)
      },
      hasFilter () {
        return !!(this.categoryKey || (this.keyword || '').trim())
      },
      filteredEntries () {
        let list = this.entries || []
        if (this.categoryKey) {
          list = list.filter(e => e.categoryKey === this.categoryKey)
        }
        let kw = (this.keyword || '').trim().toLowerCase()
        if (!kw) {
          return list
        }
        return list.filter(e => {
          let blob = [e.title, e.summary].concat(e.keywords || []).join(' ').toLowerCase()
          return blob.indexOf(kw) !== -1
        })
      }
    },
    watch: {
      groupId: {
        immediate: true,
        handler (val) {
          if (Number(val) === 1) {
            this.ensureLoaded()
            this.syncExpandByFolder()
          } else {
            this.categories = []
            this.entries = []
            this.loadedGroupId = null
          }
        }
      },
      currentFolderId () {
        this.syncExpandByFolder()
      },
      rootNodeId () {
        this.syncExpandByFolder()
      }
    },
    methods: {
      syncExpandByFolder () {
        if (!this.visible) {
          return
        }
        if (this.atRoot) {
          // 根目录：若用户未主动收起则展开
          if (!this.userCollapsed) {
            this.expanded = true
          }
        } else {
          // 子目录自动收起；清除「用户收起」标记以免回根仍收起
          this.expanded = false
          this.userCollapsed = false
        }
      },
      expand () {
        this.expanded = true
        this.userCollapsed = false
        this.ensureLoaded()
      },
      collapse () {
        this.expanded = false
        if (this.atRoot) {
          this.userCollapsed = true
        }
      },
      toggleCategory (key) {
        this.categoryKey = this.categoryKey === key ? '' : key
      },
      clearFilter () {
        this.categoryKey = ''
        this.keyword = ''
      },
      onKeywordChange () {
        // v-model 已更新；过滤靠 computed
      },
      ensureLoaded () {
        if (!this.visible) {
          return
        }
        if (this.loadedGroupId === Number(this.groupId) && (this.entries.length || this.categories.length) && !this.loadError) {
          return
        }
        this.reload()
      },
      reload () {
        let self = this
        if (!self.visible) {
          return
        }
        self.loading = true
        self.loadError = false
        self.$http.get('/disk/nav/config', {params: {groupId: 1}}).then(function (res) {
          self.loading = false
          if (res && res.status === 'ok' && res.data) {
            self.categories = res.data.categories || []
            self.entries = res.data.entries || []
            self.loadedGroupId = 1
            self.loadError = false
          } else {
            self.loadError = true
          }
        }).catch(function () {
          self.loading = false
          self.loadError = true
        })
      },
      openNode (nodeId) {
        let self = this
        if (!nodeId) {
          self.$Message.error('该入口已不可用，请联系维护人更新导航')
          return
        }
        let seq = ++self.openSeq
        self.openingId = nodeId
        self.$http.get('/disk/nav/open', {params: {groupId: 1, nodeId: nodeId}}).then(function (res) {
          if (seq !== self.openSeq) {
            return
          }
          self.openingId = null
          if (res && res.status === 'ok' && res.data) {
            self.$emit('open-target', res.data)
          }
          // 非 ok：Http 拦截器已 Message.error(data.msg)，此处不再重复弹
        }).catch(function () {
          if (seq !== self.openSeq) {
            return
          }
          self.openingId = null
          // 网络层错误拦截器已提示「系统繁忙」
        })
      }
    }
  }
</script>

<style scoped>
  .disk-group-nav {
    margin-bottom: 8px;
  }
  .disk-group-nav-bar {
    position: sticky;
    top: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 38px;
    padding: 0 12px;
    background: #fff;
    border-bottom: 1px solid #E8EAEC;
  }
  .disk-group-nav-bar-left {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #515a6e;
    font-size: 13px;
  }
  .disk-group-nav-panel {
    background: #F7F8FA;
    border-radius: 6px;
    padding: 12px 16px 16px;
  }
  .disk-group-nav-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }
  .disk-group-nav-title strong {
    font-size: 14px;
    color: #17233d;
  }
  .disk-group-nav-link {
    color: #2d8cf0;
    cursor: pointer;
    font-size: 13px;
  }
  .disk-group-nav-cats {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 10px;
  }
  .disk-group-nav-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 14px;
    background: #fff;
    border: 1px solid #DCDEE2;
    color: #515a6e;
    font-size: 12px;
    cursor: pointer;
  }
  .disk-group-nav-chip.on {
    border-color: #2d8cf0;
    color: #2d8cf0;
    background: #F0F8FF;
  }
  .disk-group-nav-chip-open {
    color: #2d8cf0;
    font-size: 12px;
  }
  .disk-group-nav-search {
    display: flex;
    align-items: center;
    margin-bottom: 12px;
  }
  .disk-group-nav-empty {
    color: #808695;
    font-size: 13px;
    padding: 12px 0;
  }
  .disk-group-nav-cards {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
  }
  @media (max-width: 1200px) {
    .disk-group-nav-cards {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  @media (max-width: 800px) {
    .disk-group-nav-cards {
      grid-template-columns: 1fr;
    }
  }
  .disk-group-nav-card {
    background: #fff;
    border: 1px solid #E8EAEC;
    border-radius: 6px;
    padding: 12px;
    cursor: pointer;
  }
  .disk-group-nav-card:hover {
    border-color: #2d8cf0;
  }
  .disk-group-nav-card-title {
    font-size: 13px;
    font-weight: 600;
    color: #17233d;
    margin-bottom: 6px;
  }
  .disk-group-nav-card-summary {
    font-size: 12px;
    color: #515a6e;
    line-height: 1.4;
    min-height: 34px;
    margin-bottom: 6px;
  }
  .disk-group-nav-card-path {
    font-size: 12px;
    color: #808695;
    margin-bottom: 8px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .disk-group-nav-card-action {
    text-align: right;
  }
</style>



// WEBPACK FOOTER //
// DiskGroupNav.vue?6fd6b6b0